import React from 'react';
import type { InsuranceProviderPlanDetailViewModel, ServiceCodeMappingViewModel } from '@generated';

interface ServiceRatesTabProps {
	plan: InsuranceProviderPlanDetailViewModel;
}

const serviceTypeColors: Record<number, string> = {
	1: 'bg-blue-50 text-blue-700',
	2: 'bg-green-50 text-green-700',
	3: 'bg-purple-50 text-purple-700',
	4: 'bg-orange-50 text-orange-700',
	5: 'bg-pink-50 text-pink-700',
	6: 'bg-teal-50 text-teal-700'
};

function formatCurrency(amount: number) {
	return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export function ServiceRatesTab({ plan }: ServiceRatesTabProps) {
	const mappings = plan.serviceCodeMappings;

	return (
		<div className="mt-6">
			<div className="rounded-xl border border-gray-200 bg-white shadow-sm">
				<div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
					<div className="flex items-center justify-between">
						<h3 className="text-sm font-semibold text-gray-700">Service Code Mappings</h3>
					</div>
				</div>

				{mappings.length === 0 ? (
					<div className="p-6 text-center text-sm text-gray-500">No service code mappings configured.</div>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-left text-sm">
							<thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
								<tr>
									<th className="px-4 py-3">Service Type</th>
									<th className="px-4 py-3">Revenue Code</th>
									<th className="px-4 py-3">HCPCS</th>
									<th className="px-4 py-3">Modifier</th>
									<th className="px-4 py-3">Covered</th>
									<th className="px-4 py-3">EVV</th>
									<th className="px-4 py-3">Current Rate</th>
									<th className="px-4 py-3">Previous Rate</th>
									<th className="px-4 py-3">Status</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-100">
								{mappings.map(mapping => (
									<ServiceCodeRow key={mapping.id} mapping={mapping} />
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}

function ServiceCodeRow({ mapping }: { mapping: ServiceCodeMappingViewModel }) {
	const colorClass = serviceTypeColors[mapping.disciplineId] ?? 'bg-gray-50 text-gray-700';

	return (
		<tr className={mapping.active ? '' : 'opacity-50'}>
			<td className="px-4 py-3">
				<span className={`rounded px-2 py-0.5 text-xs font-medium ${colorClass}`}>{mapping.disciplineLabel}</span>
			</td>
			<td className="px-4 py-3 font-mono">{mapping.revenueCode}</td>
			<td className="px-4 py-3 font-mono">{mapping.hcpcsCode}</td>
			<td className="px-4 py-3 font-mono">{mapping.modifier ?? '-'}</td>
			<td className="px-4 py-3">
				{mapping.isCovered ? (
					<span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">Yes</span>
				) : (
					<span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-700">No</span>
				)}
			</td>
			<td className="px-4 py-3">
				{mapping.evvRequired ? (
					<span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700">Required</span>
				) : (
					<span className="text-xs text-gray-400">-</span>
				)}
			</td>
			<td className="px-4 py-3">
				<div>
					<span className="font-medium">{formatCurrency(mapping.currentRate.netAmount)}</span>
					<span className="ml-1 text-xs text-gray-400">/ {mapping.currentRate.rateUnit}</span>
				</div>
				<div className="text-xs text-gray-400">Eff. {mapping.currentRate.effectiveDate.toLocaleDateString()}</div>
			</td>
			<td className="px-4 py-3">
				{mapping.previousRate ? (
					<div>
						<span className="text-gray-500">{formatCurrency(mapping.previousRate.netAmount)}</span>
						<div className="text-xs text-gray-400">Eff. {mapping.previousRate.effectiveDate.toLocaleDateString()}</div>
					</div>
				) : (
					<span className="text-xs text-gray-400">-</span>
				)}
			</td>
			<td className="px-4 py-3">
				{mapping.active ? (
					<span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">Active</span>
				) : (
					<span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">Inactive</span>
				)}
			</td>
		</tr>
	);
}
